export type Period = "WEEKLY" | "MONTHLY" | "CUSTOM";

export type PeriodRange =
  | { type: "MONTHLY"; year: number; month: number }
  | { type: "WEEKLY"; weekStart: string; weekEnd: string }
  | { type: "CUSTOM"; startDate: string | null; endDate: string | null };

/** @deprecated Use `Period` */
export type DownloadPeriod = Period;

/** @deprecated Use `PeriodRange` */
export type DownloadDateRange = PeriodRange;

export const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const MONTH_OPTIONS = MONTH_LABELS.map((label, i) => ({
  value: String(i + 1),
  label,
}));

export const PERIOD_OPTIONS = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "CUSTOM", label: "Custom" },
];

export function toIsoDate(d: Date): string {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Week starts Monday (ISO-style weekday alignment). */
export function startOfWeekMonday(ref: Date): Date {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

export function endOfWeekMonday(weekStart: Date): Date {
  const e = new Date(weekStart);
  e.setDate(e.getDate() + 6);
  e.setHours(0, 0, 0, 0);
  return e;
}

export function weeklyRangeForReference(
  ref: Date,
): Extract<PeriodRange, { type: "WEEKLY" }> {
  const start = startOfWeekMonday(ref);
  const end = endOfWeekMonday(start);
  return {
    type: "WEEKLY",
    weekStart: toIsoDate(start),
    weekEnd: toIsoDate(end),
  };
}

export function monthlyForReference(
  ref: Date,
): Extract<PeriodRange, { type: "MONTHLY" }> {
  return {
    type: "MONTHLY",
    year: ref.getFullYear(),
    month: ref.getMonth() + 1,
  };
}

export function defaultRangeForPeriod(period: Period): PeriodRange {
  const now = new Date();
  if (period === "MONTHLY") return monthlyForReference(now);
  if (period === "WEEKLY") return weeklyRangeForReference(now);
  return { type: "CUSTOM", startDate: null, endDate: null };
}

export function periodRangeToApiDates(
  range: PeriodRange,
): { from: string; to: string } | null {
  if (range.type === "MONTHLY") {
    const start = new Date(range.year, range.month - 1, 1);
    const end = new Date(range.year, range.month, 0);
    return { from: toIsoDate(start), to: toIsoDate(end) };
  }
  if (range.type === "WEEKLY") {
    return { from: range.weekStart, to: range.weekEnd };
  }
  if (range.type === "CUSTOM" && range.startDate && range.endDate) {
    return { from: range.startDate, to: range.endDate };
  }
  return null;
}

export function formatWeekLabel(
  weekStartIso: string,
  weekEndIso: string,
): string {
  const a = new Date(`${weekStartIso}T12:00:00`);
  const b = new Date(`${weekEndIso}T12:00:00`);
  const opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  return `${a.toLocaleDateString(undefined, opts)} – ${b.toLocaleDateString(undefined, opts)}`;
}
